import { Box, Stack, Typography } from '@mui/material';
import { IconTile } from 'src/components/IconTile';
import { SETTINGS_SECTION_ROW_PADDING } from './settings-section-row.constants';
import { SettingsSectionRowProps } from './settings-section-row.types';

/**
 * One setting inside a SettingsSectionCard: optional icon tile + title +
 * secondary line + a caller-supplied trailing control. The row is
 * presentational only — the control owns its own state and handlers.
 *
 * With a secondary line the icon and control align to the title (flex-start),
 * so multi-line text flows below them without re-centering either edge. A row
 * with only a title is a single line shorter than the icon tile, so it centers
 * instead — otherwise the text hangs off the top of the tile.
 *
 * The control lives in the same column as the text (right of it, next to the
 * icon), so with `mobileControlLayout="below"` it drops under the text on mobile
 * (xs) sharing the text's left edge — not indented under the leading icon — and
 * returns inline from `sm` up. Use it for wide controls (e.g. Behavior selects).
 */
export const SettingsSectionRow = ({
  icon,
  title,
  secondary,
  control,
  controlAlign = 'start',
  mobileControlLayout = 'inline',
}: SettingsSectionRowProps) => {
  const stackBelowOnMobile = mobileControlLayout === 'below';
  const controlSelf = controlAlign === 'center' ? 'center' : 'flex-start';
  // A title-only row is one line tall, so centering keeps it on the icon's
  // midline. Once the control drops below the text on mobile the row is
  // multi-line again, so it goes back to top-aligned there.
  const centeredAlign = stackBelowOnMobile ? { xs: 'flex-start', sm: 'center' } : 'center';
  const rowAlign = secondary ? 'flex-start' : centeredAlign;

  return (
    <Stack
      direction="row"
      alignItems={rowAlign}
      spacing={1.5}
      sx={SETTINGS_SECTION_ROW_PADDING}
    >
      {icon ? <IconTile>{icon}</IconTile> : null}
      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: stackBelowOnMobile ? { xs: 'column', sm: 'row' } : 'row',
          alignItems: 'flex-start',
          gap: 1.5,
        }}
      >
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} color="custom.OV.sys.onSurface">
            {title}
          </Typography>
          {secondary ? (
            <Typography
              variant="body2"
              color="custom.OV.sys.onSurfaceVariant"
              sx={{ mt: 0.5 }}
            >
              {secondary}
            </Typography>
          ) : null}
        </Box>
        {control ? (
          <Box
            sx={{
              flexShrink: 0,
              alignSelf: stackBelowOnMobile
                ? { xs: 'flex-start', sm: controlSelf }
                : controlSelf,
            }}
          >
            {control}
          </Box>
        ) : null}
      </Box>
    </Stack>
  );
};

SettingsSectionRow.displayName = 'SettingsSectionRow';
